/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { LocalizedMessage, LocalizedStandardDateNoSeconds } from 'intl/LocalizedComponents';
import _get from 'lodash/get';
import { withTheme } from '@material-ui/core/styles';
import { withRouter, hashHistory } from 'react-router';
// Core components
import Box from 'components/core/Box';
import { ContainedButton } from 'components/core/Button';
import Typography from 'components/core/Typography';
import TextField from 'components/core/TextField';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
// Models
import mFinance from 'models/cpm/finance';
import mOnDemand from 'models/cpm/onDemand';
// Custom
import {
  cpmPurchaseShape,
  cpmSlugCustomizationShape,
  cpmOnDemandAccessForCheckoutDataShape,
} from 'constants/PropTypesShapes';
import { cpmInitiatePayment, cpmUpdateOnDemandAccessCustomer } from 'actions';
import { dateGenerator } from 'utils/DateUtils';
import { buildFormErrors, cpmCheckoutOnDemandAccessConstraints } from 'utils/Forms';
import InformationItem from 'components/items/InformationItem';
import { BackContainedButton } from 'components/buttons/ContainedButtons';
import PurchaseInfoPrice from 'components/finance/PurchaseInfoPrice';
import CheckoutHeader from 'cpmCheckout/assets/CheckoutHeader';
import { navigateToCheckout } from 'cpmCheckout/navigation';
import { navigateToOnDemandShow } from 'cpmMyOnDemandAccess/navigation';

const PurchaseWrapperOnDemand = ({

  location: { query },
  slugId,

  purchase,
  product,
  children: PaymentForm,

  customization,
  theme,

  cpmFinancePayment,
  dispatchInitiatePayment,

  dispatchUpdateOnDemandAccessCustomer,

  cpmOnDemandUpdated,

  fields,
  errors,
  onFieldsChange,
  onErrorsChange,
}) => {
  const { end: endOffer } = purchase.offers[0];
  const {
    begin,
    end,
    carpark: { onDemandExitCourtesyDelay },
    reference,
  } = product;
  const hasRunOnDemandUpdate = useRef(false); // This ref keeps track of whether the code has run once

  // Check offer type
  const isDefinitive = mFinance.purchaseOfferTypeIsOndemandDefinitive(purchase.offers[0]);
  const isTemporary = mFinance.purchaseOfferTypeIsOndemandTemporary(purchase.offers[0]);
  const isExtend = mFinance.purchaseOfferTypeIsOndemandExtend(purchase.offers[0]);
  const isOverstay = mFinance.purchaseOfferTypeIsOndemandOverstay(purchase.offers[0]);

  // Check if offer is 0 or free
  const offerIsFree = mFinance.offerIsFree(purchase.offers[0]) || purchase.offers[0].totalAmountInclTax === 0;
  const requirePayment = !offerIsFree;

  // Use effect only for free offer, otherwise the on-demand customer update and
  // the payment initiation is performed in the StripePaymentForm component
  if (!requirePayment) {
    useEffect(() => {
      if (cpmOnDemandUpdated && !hasRunOnDemandUpdate.current) {
        dispatchInitiatePayment({
          purchaseId: purchase.id,
          savePaymentMethodForFutureUsage: false,
        });

        hasRunOnDemandUpdate.current = true; // Mark the code block as having run
      }

      // Redirect to on-demand summary
      if (cpmFinancePayment) {
        const { paymentOptions } = mFinance.getProductPurchasableOnDemand;
        hashHistory.push(`${paymentOptions.successURI}/${reference}?operation=${paymentOptions.operationSuccess}`);
      }
    }, [cpmFinancePayment, cpmOnDemandUpdated]);
  }

  /* Update customer information */
  /* --------------------------- */

  const updateCustomerInformation = () => {
    const { name, email } = fields;

    const clientFormErrors = buildFormErrors({ name, email }, cpmCheckoutOnDemandAccessConstraints);
    onErrorsChange(clientFormErrors);

    if (clientFormErrors) return;
    // Update on demand informations
    dispatchUpdateOnDemandAccessCustomer({
      email,
      lastname: name,
      reference: query.onDemandReference,
      slugId,
    });
  };

  /* Initiate payment */
  /* -------------------------- */
  const initiatePayment = () => {
    dispatchInitiatePayment({
      purchaseId: purchase.id,
      savePaymentMethodForFutureUsage: false,
    });
  };

  // 🧱 Components

  /* -------------- */
  /* Logo and title */
  /* -------------- */

  const LogoAndTitle = customization ? <CheckoutHeader customization={customization} /> : null;

  /* -------------- */
  /*     Dates      */
  /* -------------- */

  const iconColor = theme.palette.primary.main;

  /*     Begin      */
  /* -------------- */

  const Begin = (
    <InformationItem
      label={(
        <Typography type="infoItemLabel">
          {(isExtend || isOverstay) && <LocalizedMessage id="field.entryDateCarpark" />}
          {(isDefinitive || isTemporary) && <LocalizedMessage id="field.entryDate" />}
        </Typography>
 )}
      icon="itemDateBegin"
      iconColor={iconColor}
      value={(
        <Typography type="infoItemSubtitle">
          <LocalizedStandardDateNoSeconds value={dateGenerator.date(begin)} />
        </Typography>
)}
      noMargin
    />

  );

  /*  Initial end   */
  /* -------------- */

  const InitialEnd = isOverstay ? (
    <InformationItem
      label={(
        <Typography type="infoItemLabel">
          <LocalizedMessage id="field.plannedExitDateInitial" />
        </Typography>
                  )}
      icon="itemDateEnd"
      iconColor={iconColor}
      value={(
        <Typography type="infoItemSubtitle">
          <LocalizedStandardDateNoSeconds value={dateGenerator.date(end)} />
        </Typography>
                  )}
      noMargin
    />

  ) : null;

  /*     End      */
  /* ------------- */

  const End = (
    <InformationItem
      label={(
        <Typography type="infoItemLabel">
          {(isExtend || isOverstay) && <LocalizedMessage id="field.newExitDateDefinitive" />}
          {(isDefinitive || isTemporary) && <LocalizedMessage id="field.exitDefinitiveDate" />}
        </Typography>
                )}
      icon="itemDateEnd"
      iconColor={iconColor}
      value={(
        <Typography type="infoItemSubtitle">
          <LocalizedMessage
            id="field.exitCourtesyDelayDateTime"
            values={{
              date: <LocalizedStandardDateNoSeconds value={dateGenerator.date(endOffer)} />,
              delay: onDemandExitCourtesyDelay,
            }}
          />
        </Typography>
                )}
      noMargin
    />

  );

  const Dates = (
    <div className="w-100 mt-3">
      {Begin}
      {InitialEnd}
      {End}
    </div>
  );

  /* -------------- */
  /*     Price      */
  /* -------------- */

  const Price = (
    <div className="w-100 mt-3">
      <PurchaseInfoPrice
        data={purchase}
        purchases={product.purchases}
        type={
          /* eslint-disable */
          isExtend   ? 'extension' :
          isOverstay ? 'overstay'  :
          'initial'}
          /* eslint-enable */
      />
    </div>
  );

  /* ---------------- */
  /*      Payment     */
  /* ---------------- */

  /*  Checkout title  */
  /* ---------------- */

  const CheckoutTitle = (
    <div className="mb-3 text-center checkoutCardTitle">
      {isDefinitive && <LocalizedMessage id="info.checkoutDefinitiveExit.payment" />}
      {isTemporary && <LocalizedMessage id="info.checkoutTemporaryExit.payment" />}
      {isExtend && <LocalizedMessage id="info.checkoutExtend.payment" />}
      {isOverstay && <LocalizedMessage id="info.payOverstay" />}
    </div>
  );

  /*  Form for additional informations  */
  /* ---------------------------------- */

  const UpdateButton = (
    <ContainedButton
      className="text-center mt-3"
      label={<LocalizedMessage id="button.validate" />}
      onClick={updateCustomerInformation}
    />
  );

  const CustomerInformationsForm = (
    <form noValidate>
      <div className="w-100 text-center my-3 checkoutCardSubtitle">
        <LocalizedMessage id="info.onDemandInfo.subTitle" />
      </div>
      <div className="px-4">
        <TextField
          key="name"
          label={<LocalizedMessage id="field.name" />}
          value={fields.name}
          onChange={(value) => onFieldsChange({ name: value })}
          errorText={_get(errors, 'name')}
          required
        />
        <TextField
          key="company"
          label={<LocalizedMessage id="field.company" />}
          value={fields.company}
          onChange={(value) => onFieldsChange({ company: value })}
          errorText={_get(errors, 'company')}
          required
        />
        <TextField
          type="email"
          name="email"
          label={<LocalizedMessage id="field.email" />}
          value={fields.email}
          errorText={_get(errors, 'email')}
          onChange={(value) => onFieldsChange({ email: value })}
          required
        />
      </div>
      {UpdateButton}
    </form>
  );

  /*  Validation form  */
  /* ----------------- */

  const ValidateButton = (
    <ContainedButton
      className="text-center"
      label={<LocalizedMessage id="button.validate" />}
      onClick={initiatePayment}
    />
  );

  const ValidationForm = (
    <React.Fragment>
      <div className="text-center my-3 checkoutCardSubtitle">
        {/* eslint-disable */}
        {isExtend   ? <LocalizedMessage id="info.onDemandExtend.choiceSubTitle" /> :
         isOverstay ? <LocalizedMessage id="info.onDemandInfo.freeOverstay.subTitle" /> : null}
        {/* eslint-enable */}
      </div>
      {ValidateButton}
    </React.Fragment>
  );

  const Payment = (
    <div className="mt-3 w-100 bookCard">
      {CheckoutTitle}
      {/* eslint-disable */}
      {requirePayment               ? PaymentForm :
      (isDefinitive || isTemporary) ? CustomerInformationsForm :
                                      ValidationForm}
      {/* eslint-enable */}
    </div>
  );

  /* -------------------- */
  /*      Back button     */
  /* -------------------- */

  const shouldGoBackToCheckout = isDefinitive || isTemporary;
  const shouldGoBackToOnDemand = isExtend || isOverstay;

  const BackButton = (shouldGoBackToCheckout || shouldGoBackToOnDemand) ? (
    <BackContainedButton
      className="mt-5 text-center"
      color="secondary"
      onClick={() => (
        /* eslint-disable */
        shouldGoBackToCheckout ? navigateToCheckout() :
        shouldGoBackToOnDemand ? navigateToOnDemandShow(reference) : null
        /* eslint-enable */
      )}
    />
  ) : null;

  /* -------------------- */
  /*         UI           */
  /* -------------------- */
  const MAX_WIDTH = 430;

  return (
    <Box type="defaultBackground" className="d-flex flex-column mx-auto align-items-center px-3" style={{ maxWidth: MAX_WIDTH }}>
      {LogoAndTitle}
      {Dates}
      {Price}
      {Payment}
      {BackButton}
    </Box>
  );
};

PurchaseWrapperOnDemand.propTypes = {
  location: PropTypes.shape({
    query: PropTypes.object,
  }).isRequired,

  children: PropTypes.node.isRequired,
  theme: PropTypes.object.isRequired,

  purchase: cpmPurchaseShape.isRequired,
  product: cpmOnDemandAccessForCheckoutDataShape.isRequired,

  /* 🎪 Store */
  slugId: PropTypes.number.isRequired,
  customization: cpmSlugCustomizationShape,

  cpmFinancePayment: cpmPurchaseShape,
  dispatchInitiatePayment: PropTypes.func.isRequired,

  cpmOnDemandUpdated: PropTypes.bool,
  dispatchUpdateOnDemandAccessCustomer: PropTypes.func.isRequired,

  /* 📝 Form */
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
};

PurchaseWrapperOnDemand.defaultProps = {
  /* 📝 Form */
  errors: null,

  /* 🎪 Store */
  customization: null,
  cpmFinancePayment: null,
  cpmOnDemandUpdated: false,
};

const mapStateToProps = (state) => ({
  slugId: _get(state, 'cpmSlug.current.result.id'),
  customization: _get(state, 'cpmSlug.current.result.customization'),
  cpmOnDemandUpdated: _get(state, 'cpmOnDemand.update.updated'),
  cpmOnDemandUpdateError: _get(state, 'cpmOnDemand.update.error'),
  cpmFinancePayment: _get(state, 'cpmFinance.purchase.result'),
});

const mapFieldsToProps = {
  name: { defaultValue: '' },
  company: { defaultValue: '' },
  email: { defaultValue: '' },
};

const mapOwnPropsToResources = (ownProps) => ({
  operationError: {
    ...ownProps.cpmOnDemandUpdateError,
  },
});

const mapDispatchToProps = (dispatch) => ({
  dispatchInitiatePayment: (data) => dispatch(cpmInitiatePayment(mFinance.mutInitiatePayment, data)),
  dispatchUpdateOnDemandAccessCustomer: (data) => dispatch(cpmUpdateOnDemandAccessCustomer(mOnDemand.mutUpdateOnDemandAccessCustomer, data)),
});

module.exports = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withFormManagement(mapFieldsToProps, mapOwnPropsToResources, false),
  withTheme,
  withRouter,
)(PurchaseWrapperOnDemand);



// WEBPACK FOOTER //
// ./routes/purchase/assets/PurchaseWrapperOnDemand.js